/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Box, Alert, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Chip, CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { consultarCNPJ, listarEmpresas, atualizarEmpresa, vincularEmpresaAoGrupo } from '../../services/api-empresas.service';
import BlueButton from '../BlueButton';
import ModalCadastroEmpresa from './ModalCadastroEmpresa';

// Função para formatar CNPJ manualmente
const formatarCNPJ = (value) => {
    const cnpj = value.replace(/\D/g, '');
    if (cnpj.length <= 2) return cnpj;
    if (cnpj.length <= 5) return `${cnpj.slice(0, 2)}.${cnpj.slice(2)}`;
    if (cnpj.length <= 8) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5)}`;
    if (cnpj.length <= 12) return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8)}`;
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
};

const ModalGrupoEconomico = ({ open, onClose, matriz }) => {
    const [empresasGrupo, setEmpresasGrupo] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);

    // Search State
    const [cnpjSearch, setCnpjSearch] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const [error, setError] = useState('');

    // Confirmation State (Local Found)
    const [confirmLink, setConfirmLink] = useState(null);

    // Modal Wizard State (New Branch) - Legacy name kept for safety but means New Company
    const [newBranchData, setNewBranchData] = useState(null);
    const [openWizard, setOpenWizard] = useState(false);

    useEffect(() => {
        if (open && matriz) {
            loadEmpresasGrupo();
            resetSearch();
        }
    }, [open, matriz]);

    const loadEmpresasGrupo = async () => {
        if (!matriz?.id_grupo_economico) return;
        setLoading(true);
        try {
            const response = await listarEmpresas({ id_grupo_economico: matriz.id_grupo_economico, limit: 500, offset: 0 });
            const lista = response?.dados != null ? response.dados : (Array.isArray(response) ? response : []);
            setEmpresasGrupo(lista);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const resetSearch = () => {
        setCnpjSearch('');
        setSearchResult(null);
        setError('');
        setAdding(false);
        setConfirmLink(null);
        setNewBranchData(null);
        setOpenWizard(false);
    };

    const handleSearch = async () => {
        if (cnpjSearch.length < 18) return;
        setAdding(true);
        setError('');
        setConfirmLink(null);

        try {
            // 1. Tenta buscar no banco local
            const localResponse = await listarEmpresas({ cnpj: cnpjSearch });
            const localLista = localResponse?.dados != null ? localResponse.dados : (Array.isArray(localResponse) ? localResponse : []);

            if (localLista.length > 0) {
                // Existe no banco!
                const existingCompany = localLista[0];

                // Validações
                // Verifica se já pretence ao MESMO grupo
                if (existingCompany.id_grupo_economico === matriz.id_grupo_economico) {
                    setError('Esta empresa já pertence a este grupo econômico.');
                    setAdding(false);
                    return;
                }

                // Se pertence a OUTRO grupo, avisa? O backend deve tratar, mas podemos avisar.
                if (existingCompany.id_grupo_economico) {
                    setError(`Esta empresa já pertence ao grupo: ${existingCompany.nome_grupo_economico}.`);
                    setAdding(false);
                    return;
                }

                // Pede confirmação para VINCULAR
                setConfirmLink(existingCompany);
                setAdding(false);
                return;
            }

            // 2. Não existe no banco? Busca na API Externa via Service
            const data = await consultarCNPJ(cnpjSearch);

            // Abre o Wizard de Cadastro preenchido
            setNewBranchData({
                ...data,
                is_matriz: false,
                // matriz_id: matriz.id, // REMOVIDO: Não usamos mais matriz_id forçado aqui, o vínculo vem depois
                id_grupo_economico: matriz.id_grupo_economico, // Preenche grupo
                status: 'ATIVO',
                regime_tributario: 'Real'
            });
            setOpenWizard(true);

        } catch (err) {
            setError('Empresa não encontrada na Receita ou erro na consulta.');
        } finally {
            setAdding(false);
        }
    };

    const handleConfirmLink = async () => {
        if (!confirmLink) return;
        try {
            setLoading(true);
            // Vincula ao Grupo Econômico
            await vincularEmpresaAoGrupo(matriz.id_grupo_economico, confirmLink.id);

            alert(`Empresa "${confirmLink.razao_social}" vinculada ao grupo com sucesso!`);
            resetSearch();
            loadEmpresasGrupo();
        } catch (err) {
            setError('Erro ao vincular empresa: ' + (err.response?.data?.erro || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleWizardSuccess = () => {
        setOpenWizard(false);
        resetSearch();
        loadEmpresasGrupo();
    };

    if (!matriz) return null;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#0d47a1', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon /> Empresas do Grupo Econômico
                </DialogTitle>

                <DialogContent dividers>
                    {/* Header Grupo */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                        <Typography variant="h6" color="primary">{matriz.nome_grupo_economico || 'Grupo Econômico'}</Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Grupo Econômico</strong> (ID: {matriz.id_grupo_economico})
                        </Typography>
                    </Box>

                    {/* Adicionar Filial */}
                    <Box sx={{ mb: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <AddCircleIcon fontSize="small" sx={{ mr: 1 }} /> Adicionar Empresa ao Grupo
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <TextField
                                label="CNPJ da Filial ou Empresa Relacionada"
                                value={cnpjSearch}
                                onChange={(e) => setCnpjSearch(formatarCNPJ(e.target.value))}
                                fullWidth
                                size="small"
                                error={!!error}
                                helperText={error}
                                sx={{ bgcolor: 'white' }}
                                placeholder="00.000.000/0000-00"
                                inputProps={{ maxLength: 18 }}
                            />
                            <BlueButton
                                textValue={adding ? "Buscando..." : "Buscar e Adicionar"}
                                clickFunction={handleSearch}
                                disabled={adding || cnpjSearch.length < 18}
                                style={{ height: 40, whiteSpace: 'nowrap' }}
                            />
                        </Box>

                        {/* Confirmação de Vínculo */}
                        {confirmLink && (
                            <Alert
                                severity="warning"
                                action={
                                    <Button color="inherit" size="small" onClick={handleConfirmLink}>
                                        VINCULAR AGORA
                                    </Button>
                                }
                                sx={{ mt: 2 }}
                            >
                                A empresa <strong>{confirmLink.razao_social}</strong> já existe.
                                Deseja vinculá-la como filial deste grupo?
                            </Alert>
                        )}
                    </Box>

                    {/* Lista de Empresas */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Empresas Vinculadas ({empresasGrupo.length})
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableRow>
                                        <TableCell>CNPJ</TableCell>
                                        <TableCell>Razão Social</TableCell>
                                        <TableCell>Situação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {empresasGrupo.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                Nenhuma empresa vinculada.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        empresasGrupo.map((filial) => (
                                            <TableRow key={filial.id}>
                                                <TableCell>{filial.cnpj}</TableCell>
                                                <TableCell>{filial.razao_social}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={filial.ativo ? "Ativa" : "Inativa"}
                                                        size="small"
                                                        color={filial.ativo ? "success" : "default"}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} variant="contained" color="success">Concluir</Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Cadastro (Wizard) para Nova Filial */}
            <ModalCadastroEmpresa
                open={openWizard}
                onClose={() => setOpenWizard(false)}
                onSuccess={handleWizardSuccess}
                initialData={newBranchData}
            />
        </>
    );
};

export default ModalGrupoEconomico;
